import { Fragment, type ComponentProps, type ReactNode } from 'react'

import { cx } from '@/libs/cva'

import { Form } from './form'

interface ControlWrapperProps {
	noControl?: boolean
	children: ReactNode
}

function ControlWrapper({ children, noControl }: ControlWrapperProps) {
	const Control = noControl ? Fragment : Form.Control
	return <Control>{children}</Control>
}

interface FormFieldsetProps {
	className?: string
	label: string
	optional?: boolean
	children: ReactNode
	noLabel?: boolean
	noControl?: boolean
	noMessage?: boolean
	required?: boolean
}

function FormFieldsetOne({
	className,
	label,
	optional,
	children,
	noLabel,
	noControl,
	noMessage,
	required,
}: FormFieldsetProps) {
	return (
		<Form.Item className={cx('flex flex-col gap-y-2', className)}>
			{!noLabel && (
				<Form.Label
					className={cx({ 'sr-only': noLabel })}
					optional={optional}
					required={required}
				>
					{label}
				</Form.Label>
			)}
			<ControlWrapper noControl={noControl}>{children}</ControlWrapper>
			{!noMessage && (
				<Form.ErrorMessage className={cx({ 'sr-only': noMessage })} />
			)}
		</Form.Item>
	)
}

function WithHint({
	className,
	label,
	optional,
	children,
	noLabel,
	noControl,
	noMessage,
	hint,
}: FormFieldsetProps & { hint?: string }) {
	return (
		<Form.Item className={cx('flex flex-col gap-y-1', className)}>
			{noLabel ? null : (
				<Form.Label className={cx({ 'sr-only': noLabel })} optional={optional}>
					{label}
				</Form.Label>
			)}
			<ControlWrapper noControl={noControl}>{children}</ControlWrapper>
			{hint && <Form.Hint>{hint}</Form.Hint>}
			<Form.ErrorMessage
				className={cx({ 'sr-only': noMessage })}
			></Form.ErrorMessage>
		</Form.Item>
	)
}

function FormFieldsetRowContainer({
	className,
	...props
}: ComponentProps<'div'>) {
	return (
		<div className={cx('grid grid-cols-[100px_auto]', className)} {...props} />
	)
}

function FormFieldsetRow({
	className,
	label,
	optional,
	children,
	noControl,
	required,
}: Omit<FormFieldsetProps, 'noLabel' | 'noMessage'>) {
	return (
		<Form.Item
			className={cx('col-span-full grid grid-cols-subgrid', className)}
		>
			<Form.Label optional={optional} required={required}>
				{label} :
			</Form.Label>
			<div className="flex">
				<ControlWrapper noControl={noControl}>{children}</ControlWrapper>
			</div>
			{/* <Form.ErrorMessage /> */}
		</Form.Item>
	)
}

function FormFieldCell({
	className,
	label = 'label',
	children,
	noControl,
}: {
	className?: string
	label?: string
	children: ReactNode
	noControl?: boolean
}) {
	return (
		<Form.Item className={cx('flex flex-col gap-y-1', className)}>
			<Form.Label className="sr-only">{label}</Form.Label>
			<ControlWrapper noControl={noControl}>{children}</ControlWrapper>
		</Form.Item>
	)
}

export const FormFieldset = Object.assign(
	{},
	{
		One: FormFieldsetOne,
		WithHint,
		Row: FormFieldsetRow,
		RowContainer: FormFieldsetRowContainer,
		Cell: FormFieldCell,
	}
)
